/* eslint-disable no-console */
import { Fragment, Mark } from '@atlaskit/editor-prosemirror/model';
import type { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { convertMarkdownToProsemirror } from '@atlassian/ai-model-io/convert-markdown-to-prosemirror';
import type { IDMap } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown/serializer';

import { adfResponseToFragment } from '../../../ui/utils/request/adf-response';
import type { ADFResponse } from '../../ai-session-context/types';

/**
 * Converts ADF response to ProseMirror Fragment
 */
export function convertADFToFragment(schema: Schema, adfResponse?: ADFResponse): Fragment | null {
	if (!adfResponse) {
		return null;
	}

	try {
		const pmNode = adfResponseToFragment({ schema, adfResponse });
		return pmNode;
	} catch {
		return null;
	}
}

interface ConvertMarkdownOptions {
	/** When true, enables mentions regardless of feature gate. Defaults to feature-gated. */
	forceMentions?: boolean;
}

/**
 * Converts Markdown Plus text to a ProseMirror Fragment using the idMap for
 * rich-node resolution (mentions, etc.).
 *
 * By default, mentions are gated behind the `platform_editor_ai_draft_reply_mentions`
 * feature flag. Pass `{ forceMentions: true }` to always enable mentions
 * (used by the free-gen fallback path).
 *
 * Falls back to a plain-text paragraph on conversion failure.
 */
export function convertMarkdownToFragment(
	schema: Schema,
	markdown: string | undefined,
	idMap: IDMap | undefined,
	options?: ConvertMarkdownOptions,
): Fragment | null {
	if (!markdown) {
		return null;
	}
	try {
		const { pmFragment } = convertMarkdownToProsemirror({
			schema,
			markdown,
			idMap: (idMap as Record<string, never>) ?? {},
			featureToggles: {
				markdownPlus: true,
				markdownPlusExtensions: true,
				markdownPlusPanels: true,
				markdownPlusDecisions: true,
				markdownPlusMentions:
					options?.forceMentions || fg('platform_editor_ai_draft_reply_mentions'),
				markdownPlusRemoveUnclosedTokens: true,
				allowAttributesMatchDefault: true,
			},
		});
		return pmFragment;
	} catch {
		return Fragment.from(schema.nodes.paragraph.create(null, schema.text(markdown)));
	}
}

interface ResolveContentFragmentOptions {
	/** When true, skip conversion if markdown ends with an incomplete custom tag.
	 *  Used during streaming to avoid inserting malformed mention/custom nodes. */
	skipIncompleteCustomTags?: boolean;
}

/**
 * Resolves AI response content to a ProseMirror Fragment using a prioritised
 * conversion chain:
 *
 * 1. ADF response → Fragment (preferred when adfPrompt is enabled)
 * 2. Markdown → Fragment via experiment-gated `convertMarkdownToFragment`
 *    (respects the `show_mentions_in_suggest_reply` experiment)
 * 3. Markdown → Fragment with mentions always enabled (free-gen fallback)
 * 4. Plain-text paragraph (last resort on conversion failure)
 *
 * Returns `null` only when no content is available at all or
 * `skipIncompleteCustomTags` suppresses a partial custom tag.
 */
export function resolveContentFragment(
	schema: Schema,
	adfResponse: ADFResponse | undefined,
	markdown: string | undefined,
	idMap: IDMap | undefined,
	options?: ResolveContentFragmentOptions,
): Fragment | null {
	// 1. Try ADF first
	const adfFragment = convertADFToFragment(schema, adfResponse);
	if (adfFragment) {
		return adfFragment;
	}

	// 2. Try experiment-gated markdown conversion (uses feature-gated mentions)
	if (isExperimentEnabled('show_mentions_in_suggest_reply')) {
		const mdFragment = convertMarkdownToFragment(schema, markdown, idMap);
		if (mdFragment) {
			return mdFragment;
		}
	}

	// 3. Fallback: direct markdown conversion with mentions always enabled
	if (markdown) {
		if (options?.skipIncompleteCustomTags) {
			// Skip conversion if the markdown ends with an incomplete custom tag (e.g. <cust, <custom)
			// to avoid inserting malformed mention/custom nodes during streaming.
			const incompleteCustomTagPattern = new RegExp(
				'<(c(u(s(t(o(m(?!.*<\\/custom>).*?)?)?)?)?)?)?$',
				'gu',
			);
			const hasIncompleteCustomTag = !!markdown.match(incompleteCustomTagPattern);
			if (hasIncompleteCustomTag) {
				return null;
			}
		}

		return convertMarkdownToFragment(schema, markdown, idMap, { forceMentions: true });
	}

	return null;
}

/**
 * Why a fragment failed `node.check()`.
 *
 * Deliberately carries only schema identifiers (node and mark type names) and
 * never node text or attribute values, so it is safe to send to analytics.
 */
export type FragmentHealthFailure = {
	/** Comma-separated mark type names on the offending node, when relevant. */
	markTypes?: string;
	/** Type name of the first node that failed. */
	nodeType: string;
	reason: 'invalidMarks' | 'invalidContent' | 'invalidAttrs';
};

/**
 * Classifies why a node subtree is invalid, mirroring the checks `node.check()`
 * performs, so we can report a reason without surfacing the thrown message
 * (which can embed attribute values).
 *
 * Returns `undefined` when this node and its descendants look valid.
 */
function findHealthFailure(node: PMNode): FragmentHealthFailure | undefined {
	// `Node.check()` rebuilds the mark set with `addToSet`, which collapses
	// duplicates and applies `excludes`, then throws when the result differs.
	let normalised = Mark.none;
	for (const mark of node.marks) {
		normalised = mark.addToSet(normalised);
	}
	if (!Mark.sameSet(normalised, node.marks)) {
		return {
			reason: 'invalidMarks',
			nodeType: node.type.name,
			markTypes: node.marks.map((mark) => mark.type.name).join(','),
		};
	}

	if (!node.type.validContent(node.content)) {
		return { reason: 'invalidContent', nodeType: node.type.name };
	}

	for (let index = 0; index < node.childCount; index++) {
		const failure = findHealthFailure(node.child(index));
		if (failure) {
			return failure;
		}
	}

	return undefined;
}

/**
 * Validates fragment health by checking each node, returning why it failed.
 *
 * `node.check()` stays the source of truth for whether the fragment is usable;
 * the classification is only used to describe the failure.
 */
export function describeFragmentHealth(fragment: Fragment): FragmentHealthFailure | undefined {
	let failure: FragmentHealthFailure | undefined;

	fragment.forEach((node) => {
		if (failure) {
			return;
		}
		try {
			node.check();
		} catch {
			// `check()` also validates attrs, which `findHealthFailure` does not
			// inspect (attribute values can contain user data). If we cannot
			// attribute the failure to marks or content, report it as attrs.
			failure = findHealthFailure(node) ?? { reason: 'invalidAttrs', nodeType: node.type.name };
		}
	});

	return failure;
}

/**
 * Validates fragment health by checking each node
 */
export function fragmentIsHealthy(fragment: Fragment): boolean {
	return describeFragmentHealth(fragment) === undefined;
}

/**
 * Calculates the telepointer position by walking the nodes between startPos
 * and endPos, finding the end of the last leaf node.
 *
 * Returns endPos as fallback if no leaf nodes are found or positions are invalid.
 */
export function calculateTelepointerPos(
	doc: {
		nodesBetween: (
			from: number,
			to: number,
			fn: (node: { isLeaf: boolean; nodeSize: number }, pos: number) => void,
		) => void;
	},
	startPos: number,
	endPos: number,
): number {
	let telepointerPos = endPos;
	try {
		doc.nodesBetween(startPos, endPos, (node, pos) => {
			if (node.isLeaf) {
				telepointerPos = pos + node.nodeSize;
			}
		});
		telepointerPos = Math.min(telepointerPos, endPos);
	} catch {
		telepointerPos = endPos;
	}
	return telepointerPos;
}
